import axios from "axios";
import { userRoles } from "../../../config/userRoles";
import { FILTER_AI_API_URL } from "../../../config/constants";
import { attachOttoAuthRetry } from "./authRetry";

// Single source of truth for WHO may see Otto (the AI assistant) and its AI-Settings pages.
//
// Otto used to be super_admin-only, everywhere, with no way to widen it. The Otto User Permissions page
// (`src/components/ottoPermissions/`) changed that: a curator grants individual users the pages below,
// and those grants live in Otto's own backend (`filter-ai`), never in the Ruby permission system. This
// module is the frontend half of that gate — every mount site and every menu entry reads it, so the
// policy stays in exactly one place.
//
// The rule mirrors the app's own `checkPermission` (src/config/utils.js): super_admin short-circuits to
// true, everyone else is answered from a stored permission blob.
//
// Gate at the RENDER SITE (return null before mounting the wrapper) rather than inside the wrapper/
// FilterAssistant — that way none of the assistant's hooks or effects run for users without access.

// The grantable pages. MIRRORED IN THE BACKEND as OTTO_PAGES in
// `filter-ai/app/capabilities/otto_permissions/schemas.py` — the backend validates writes against its
// copy, so a key that exists on only one side produces either a grant nothing reads or a gate nothing
// can satisfy. Guarded by the otto-sync `xrepo-otto-pages` check; extend both sides in one change.
export const OTTO_PAGES = {
    CHAT: "otto_chat",
    KNOWLEDGE_BASE: "knowledge_base",
    ASSET_MODEL_TEMPLATES: "asset_model_templates",
    QA_QC: "qa_qc",
    // The AI Settings > Image Analyse page. `view` opens it and shows Otto's saved findings; `manage` runs
    // analyses, each of which is a paid model call per photograph.
    IMAGE_ANALYSIS: "image_analysis",
    PERMISSIONS: "otto_permissions"
};

export const OTTO_ACTIONS = { VIEW: "view", MANAGE: "manage" };

// Where the hydrated snapshot is cached between page loads.
//
// A new storage key is normally avoided for new features, and this one is a deliberate, narrow exception:
// the gate is called SYNCHRONOUSLY during render by class components that predate hooks, so it must be
// able to answer before any network call resolves — exactly the problem the app already solves for its own
// permissions with the `user_permissions` key this mirrors. The value is booleans only: no token, no
// identifier beyond the user id the app already stores, nothing that grants access on its own (every write
// is gated server-side). It is cleared on logout with the rest of the session.
const STORAGE_KEY = "otto_permissions";

// How long a hydrated snapshot is trusted before the next hydrate revalidates it against the backend.
//
// This is what makes a REVOKE take effect. The snapshot is a cache for the synchronous first paint, NOT a
// source of truth: without an expiry, the first successful hydrate pinned that user's grants forever and
// only the curator's own "force" refresh after a save could ever move them — so a revoked user kept access
// indefinitely, through reloads. Short, because until REQUIRE_AUTH is wired this gate is the only one
// actually enforcing anything. A stale snapshot still answers synchronously while the refetch is in
// flight, so revalidation never costs a flicker.
const SNAPSHOT_TTL_MS = 60 * 1000;

// One small client for the single endpoint this module reads, in the same shape as every other filter-ai
// caller here (core/service.js, knowledgeService.js, assetTemplateService.js): baseURL + withCredentials
// so the *.fcatracker.com session cookie flows. The call below deliberately passes its path as a plain
// string literal on an `...Api` instance — that is the call style the otto-sync `xrepo-proxy-paths` gate
// extracts, so this endpoint gets checked against the deployed nginx allowlist manifest like the rest.
const accessApi = axios.create({
    baseURL: FILTER_AI_API_URL,
    timeout: 20000,
    withCredentials: true
});

// A 401 here refreshes the session once and replays, like every other client in the app. It matters more
// on this one than most: a failed hydrate leaves the gate on its stale/absent snapshot, so an expired
// token would read as "you don't have Otto" and hide the launcher outright.
attachOttoAuthRetry(accessApi);

const readLocal = key => {
    try {
        return localStorage.getItem(key);
    } catch (e) {
        // localStorage can be absent or throw (embedded/incognito contexts) — never crash a render site.
        return null;
    }
};

const isSuperAdmin = () => readLocal("role") === userRoles.SUPER_ADMIN;

const currentUserId = () => readLocal("userId") || null;

// --- the snapshot ------------------------------------------------------------------------------------

// localStorage IS the snapshot; these two are only a parse cache over it, keyed on the RAW STRING —
// exactly how `getUserPermissions` in src/config/utils.js caches the app's own permission blob.
//
// Deliberately NOT a module-level snapshot that can outlive storage. Logout is `localStorage.clear()`
// followed by `history.push("/")` — a SPA navigation, with no page reload — so module state survives it.
// A remembered snapshot therefore kept answering the gate after logout, and a re-login as the same user
// matched its `userId` and never refetched: revoked access came back. Re-reading the raw string means a
// cleared storage is an immediately empty cache, on every path that ends a session, without this module
// having to know that logout happened.
let cachedRaw = null;
let cachedSnapshot = null;

// The in-flight request and WHO it is for. Both are needed: keying the dedup on the promise alone would
// hand a request started for the previous user back to the next one after a re-login.
let hydrating = null;
let hydratingFor = null;
const listeners = new Set();

const notify = () => {
    listeners.forEach(fn => {
        try {
            fn();
        } catch (e) {
            // One bad subscriber must not stop the others from learning about the change.
        }
    });
};

/**
 * The current snapshot, or null. `{ userId, isSuperAdmin, permissions, resolved, fetchedAt }`.
 *
 * Reads storage every call (cheap; the parse is cached on the raw string). Never returns a blob belonging
 * to a different user — that would answer this session's gate with someone else's grants.
 */
const readSnapshot = () => {
    const raw = readLocal(STORAGE_KEY);
    if (raw !== cachedRaw) {
        cachedRaw = raw;
        try {
            const parsed = raw ? JSON.parse(raw) : null;
            cachedSnapshot = parsed && typeof parsed === "object" ? parsed : null;
        } catch (e) {
            // A malformed blob reads as "no snapshot", which is the fail-safe direction.
            cachedSnapshot = null;
        }
    }
    if (!cachedSnapshot || cachedSnapshot.userId !== currentUserId()) return null;
    return cachedSnapshot;
};

const writeSnapshot = value => {
    try {
        if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
        else localStorage.removeItem(STORAGE_KEY);
        return true;
    } catch (e) {
        // Storage full / unavailable. Report the failure rather than pretending: with no place to persist
        // the answer the gate falls back to super_admin-only, which is the fail-closed direction.
        return false;
    }
};

const isFresh = snapshot =>
    !!snapshot && typeof snapshot.fetchedAt === "number" && Date.now() - snapshot.fetchedAt < SNAPSHOT_TTL_MS;

/** Drop everything — the session is over, or the signed-in user changed. */
export function clearOttoAccess() {
    hydrating = null;
    hydratingFor = null;
    writeSnapshot(null);
    cachedRaw = null;
    cachedSnapshot = null;
    notify();
}

/**
 * Fetch this user's grants from Otto's backend and cache them.
 *
 * REVALIDATES by default. The cached snapshot is only skipped past while it is still fresh
 * (`SNAPSHOT_TTL_MS`); once stale, every call goes to the backend, which is what lets a REVOKE reach a
 * user who is already signed in. The stale value keeps answering the gate until the new one lands, so
 * revalidation is invisible.
 *
 * Best-effort and deduplicated: a failure leaves the previous snapshot in place rather than revoking
 * access mid-session, because a network blip must not throw a curator out of a page they are working in.
 * Returns the snapshot, or null when it could not be resolved.
 */
export function hydrateOttoAccess({ force = false } = {}) {
    const userId = currentUserId();
    if (!userId) return Promise.resolve(null);
    if (!force && hydrating && hydratingFor === userId) return hydrating;
    if (!force) {
        const cached = readSnapshot();
        if (cached && cached.resolved && isFresh(cached)) return Promise.resolve(cached);
    }

    hydratingFor = userId;
    hydrating = accessApi
        .get("/otto_permissions/me", { params: { user_id: userId } })
        .then(res => {
            const data = (res && res.data) || {};
            // `resolved: false` = the backend could not identify the caller. Caching that would pin an
            // unauthoritative "no grants" answer for the session; discard it and keep the role fallback.
            if (!data.resolved) return null;
            const next = {
                userId,
                isSuperAdmin: data.is_super_admin === true,
                permissions: data.permissions || {},
                resolved: true,
                fetchedAt: Date.now()
            };
            // A response that arrives after the user changed belongs to nobody — drop it rather than
            // caching the previous session's grants against the current user.
            if (currentUserId() !== userId) return null;
            writeSnapshot(next);
            notify();
            return next;
        })
        .catch(() => null)
        .then(result => {
            if (hydratingFor === userId) {
                hydrating = null;
                hydratingFor = null;
            }
            return result;
        });

    return hydrating;
}

/**
 * Subscribe to grant changes. Returns an unsubscribe function.
 *
 * Used by the private-route shell so that when hydration lands, the menu and every mounted page re-render
 * with the correct gate instead of waiting for a navigation.
 */
export function subscribeOttoAccess(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * Test seam — inject a snapshot without a network call.
 *
 * Writes through to storage rather than setting module state, because storage IS the snapshot; a seam that
 * bypassed it would let tests pass against a shape production can no longer produce.
 */
export function __setOttoAccessSnapshot(value) {
    hydrating = null;
    hydratingFor = null;
    writeSnapshot(value && { fetchedAt: Date.now(), ...value });
    cachedRaw = null;
    cachedSnapshot = null;
    notify();
}

// --- the gate ------------------------------------------------------------------------------------------

/**
 * May this user use the given Otto page?
 *
 * Fails CLOSED for everyone except super_admin, and only after an AUTHORITATIVE answer: with no snapshot
 * yet (first paint before hydration, or a backend that cannot identify the caller) the answer is the
 * pre-existing rule — super_admin only. That way a filter-ai outage narrows Otto back to admins instead of
 * either locking them out or opening it to everyone.
 */
export function canUseOttoPage(pageKey, action = OTTO_ACTIONS.VIEW) {
    if (isSuperAdmin()) return true;

    // readSnapshot() re-reads storage, so a cleared session (logout) is an empty answer immediately —
    // it never serves a grant remembered in module state. Staleness is bounded by hydrateOttoAccess's
    // revalidation, not here: an expired snapshot still answers so the UI doesn't flicker while the
    // refetch that will replace it is in flight.
    const current = readSnapshot();
    if (!current || !current.resolved) return false;

    const actions = current.permissions && current.permissions[pageKey];
    return !!actions && actions[action] === true;
}

/** Is the signed-in user a super_admin?
 *
 * Exported because a few surfaces are for super_admins ONLY and are not expressible as an Otto page grant
 * — the QA/QC cost tile is the first: spend is operational information about the AI budget, and a curator
 * can hold `manage` on that page without being trusted with it. Reads the same stored role every other
 * gate in this module reads, so there is still one answer to "who is a super admin" rather than a second
 * localStorage lookup growing in a feature folder. The server gates the data itself (`require_admin`);
 * this only decides whether to ask for it. */
export function isOttoSuperAdmin() {
    return isSuperAdmin();
}

/** May this user see Otto's chat launcher at all? The gate every mount site calls. */
export function canUseOtto() {
    return canUseOttoPage(OTTO_PAGES.CHAT);
}

/** True when the AI Settings menu category should appear — i.e. any page under it is visible.
 *
 * Every page under the category must be listed here. Omitting one hides the whole category for a user
 * granted ONLY that page, which reads as "access was never given" — the failure the QA/QC page would have
 * hit, since a QA/QC-only grant is a realistic thing to hand a reviewer. CHAT is deliberately absent: it is
 * the assistant launcher, not a page under this menu. */
export function canSeeAiSettings() {
    return (
        canUseOttoPage(OTTO_PAGES.KNOWLEDGE_BASE) ||
        canUseOttoPage(OTTO_PAGES.ASSET_MODEL_TEMPLATES) ||
        canUseOttoPage(OTTO_PAGES.QA_QC) ||
        canUseOttoPage(OTTO_PAGES.IMAGE_ANALYSIS) ||
        canUseOttoPage(OTTO_PAGES.PERMISSIONS)
    );
}
